import React, { useMemo } from 'react'
import styled from 'styled-components'
import OriginalModal from 'react-modal'
import { Conclusion, Insight, VariablePreview } from '~/packages/general/variable/variables.typing'
import CloseIcon from '~/components/icons/ui/64px_small-remove.svg'
import { useMedia } from 'react-use'
import RightButtonsTopBox, { RightButtonsTopBoxProps } from './rightButtonsTopBox.component'
import InsightCard from './insight.component'
import useVariableItemsExport, {
  type VariableItemsExportFormat,
} from '~/prix/react/hooks/useVariableItemsExport'
import { toastError, toastSuccess } from '~/components/toasts'
import { compatibleSpaceChildren } from '~/packages/general/variable/spaceTime.data'
import { VARIABLE_ITEMS_EXPORT_DEFAULT_MAX_ITEMS } from '../variable/export/variableItemsExport.constants'
import type { ButtonsPanelButton } from './visualization/visualization.types'
import { FullscreenLoader } from '~/components/fullscreenLoader'
import { isVariableLayer } from './clusterListInput.util'

type InsightsOverlayModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  className: string
  overlayClassName: string
  style: {
    content: React.CSSProperties
  }
  children: React.ReactNode
}

const OverlayModal = OriginalModal as unknown as React.ComponentType<InsightsOverlayModalProps>

const InsightListModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease-in-out;
  box-shadow: ${({ theme }) => theme.shadow(3)};
  height: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  min-width: 400px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  transition: none;
  position: relative;
  z-index: 10001;
  padding: 0.6rem 0.6rem 0.4rem;

  .wrapper-buttons {
    display: flex;
    align-items: center;
    gap: 5px;
    position: relative;
    margin-left: auto;

    .button-style {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      height: 40px;
      width: 40px;
      cursor: pointer;
      border: none;
      border-radius: 8px;
      background: #fff;
      padding: 0;
      transition: all 0.2s ease;
      transform: scale(1);

      svg {
        margin-top: 0;
        fill: #005eb8;
        transition: all 0.2s ease;
      }

      &:hover {
        box-shadow: ${({ theme }) => theme.shadow(2)};
      }
    }
  }

  h3 {
    flex: 1;
    align-content: center;
    color: #fff;
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 1;
    margin: 0;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`

const RightButtonTopBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    max-height: calc(100vh - 256px);
    overflow: clip;
  }
`

const Container = styled.div<{ $isMobile: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${({ $isMobile }) => ($isMobile ? 'calc(100vh - 200px)' : '100%')};
  transition: none;
  padding: 0 0.6rem 0.6rem;

  scrollbar-gutter: stable;
  scrollbar-width: none;
`

const InsightsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding-bottom: 64px;
  padding-top: 0.4rem;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #666;
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;

  p {
    color: #fff;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export interface SelectedInsight extends Insight {
  baseLayerIndex: number
  insightIndex: number
  preview: VariablePreview | null
}

export interface InsightsModalProps {
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  selectedInsight: SelectedInsight | null
  onSelectInsight: React.Dispatch<React.SetStateAction<SelectedInsight | null>>
  conclusionsPerLayer: (Conclusion | null)[]
  estimatedItemsCountPerLayer?: number[]
}

export default function InsightsModal({
  isLoading,
  isOpen,
  onClose,
  selectedInsight,
  onSelectInsight,
  conclusionsPerLayer,
  estimatedItemsCountPerLayer,
  onChangeMapType,
  mapType,
  onChangeMap3DOption,
  map3DOption,
  layers,
  variables,
  setBaseLayers,
  styles,
}: InsightsModalProps & Omit<RightButtonsTopBoxProps, 'children'>) {
  const isMobile = useMedia('(max-width: 768px)')
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { exportVariableItems, isLoading: isExporting } = useVariableItemsExport()

  const exportLayerIndex = 0
  const exportMaxItems = VARIABLE_ITEMS_EXPORT_DEFAULT_MAX_ITEMS
  const exportLayerCandidate = layers[exportLayerIndex] ?? null
  const exportLayer =
    exportLayerCandidate && isVariableLayer(exportLayerCandidate) ? exportLayerCandidate : null
  const estimatedExportItemsCount =
    typeof estimatedItemsCountPerLayer?.[exportLayerIndex] === 'number'
      ? estimatedItemsCountPerLayer![exportLayerIndex]!
      : null

  const isExportItemsCompatible = useMemo(() => {
    if (!exportLayer) return false
    if (!exportLayer.variable?.actionDefinitionKey) return false
    if (
      !Array.isArray(exportLayer.variable?.detailedSelect) ||
      exportLayer.variable.detailedSelect.length === 0
    ) {
      return false
    }

    if (
      Array.isArray(exportLayer.variable?.compatibleChildrenTypes) &&
      !exportLayer.variable.compatibleChildrenTypes.includes('item')
    ) {
      return false
    }

    const delimitationType = exportLayer.space?.delimitationType
    if (!delimitationType) return false
    if (!compatibleSpaceChildren[delimitationType]?.includes('item')) return false

    return true
  }, [exportLayer])

  const exportInsight: Insight | null = useMemo(() => {
    if (!exportLayer) return null
    if (!isExportItemsCompatible) return null

    const estimated = estimatedExportItemsCount
    const hasEstimate = typeof estimated === 'number' && Number.isFinite(estimated)
    const tooLarge = hasEstimate ? estimated! > exportMaxItems : false

    const normalizeExportNoun = (text: string): string => {
      if (!text) return text
      const first = text[0] ?? ''
      const second = text[1] ?? ''
      const isLetter = (char: string) => char.toLowerCase() !== char.toUpperCase()
      if (
        isLetter(first) &&
        isLetter(second) &&
        first === first.toUpperCase() &&
        second === second.toLowerCase()
      ) {
        return first.toLowerCase() + text.slice(1)
      }
      return text
    }

    const exportNoun = normalizeExportNoun(
      exportLayer.variable?.unitPlural ?? exportLayer.variable?.unitSingular ?? 'itens',
    )

    const description = tooLarge
      ? `Download indisponível: estimativa de ${estimated!.toLocaleString('pt-BR')} ${exportNoun} (limite: ${exportMaxItems.toLocaleString(
          'pt-BR',
        )}). Ajuste os filtros para reduzir o volume.`
      : hasEstimate
        ? `Faça download dos itens apresentados nesta visualização. Estimativa atual: ${estimated!.toLocaleString(
            'pt-BR',
          )} ${exportNoun}.`
        : `Faça download dos itens apresentados nesta visualização.`

    return {
      id: 'export-items',
      type: 'exportItems',
      key: `export-items.${exportLayer.variable?.key ?? exportLayer.variable?.title ?? 'variable'}`,
      title: `Exportar ${exportNoun}`,
      description,
      relevance: 0,
      visualizations: [
        {
          renderer: 'buttons',
          payload: {
            buttons: [
              {
                label: 'Exportar para Excel',
                type: 'export',
                format: 'xlsx',
                variant: 'primary',
                disabled: Boolean(tooLarge),
              },
              {
                label: 'Exportar para CSV',
                type: 'export',
                format: 'csv',
                variant: 'primary',
                disabled: Boolean(tooLarge),
              },
            ],
          },
        },
      ],
      actions: [],
      alert: null,
      space: exportLayer.space,
      time: exportLayer.time,
    }
  }, [exportLayer, estimatedExportItemsCount, exportMaxItems, isExportItemsCompatible])

  const ensureCardFullyVisible = (insightIndex: number) => {
    const container = containerRef.current
    if (!container) return
    const cardElement = container.querySelector(
      `[data-insight-index="${insightIndex}"]`,
    ) as HTMLElement | null
    if (!cardElement) return

    const containerRect = container.getBoundingClientRect()
    const cardRect = cardElement.getBoundingClientRect()

    if (cardRect.top < containerRect.top) {
      const delta = cardRect.top - containerRect.top - 8
      container.scrollBy({ top: delta, behavior: 'smooth' })
      return
    }

    if (cardRect.bottom > containerRect.bottom) {
      const delta = cardRect.bottom - containerRect.bottom + 8
      container.scrollBy({ top: delta, behavior: 'smooth' })
    }
  }

  // Card
  const handleSelectInsight = (insight: Insight, index: number, baseLayerIndex: number) => {
    if (selectedInsight?.insightIndex === index) {
      onSelectInsight(null)
    } else {
      const initialPreview =
        (insight.actions ?? []).find(
        action => action.type === 'variablePreview',
      ) ?? null
      const newSelectedInsight: SelectedInsight = {
        ...insight,
        insightIndex: index,
        baseLayerIndex: baseLayerIndex,
        preview: initialPreview,
      }
      onSelectInsight(newSelectedInsight)

      setTimeout(() => ensureCardFullyVisible(index), 100)
    }
  }

  const handleOnClose = () => {
    onSelectInsight(null)
    onClose()
  }

  const handleVariablePreviewChange = (variablePreview: VariablePreview) => {
    onSelectInsight(previous => {
      if (!previous) return null
      return { ...previous, preview: variablePreview }
    })
  }

  const handleInsightButtonClick = async (button: ButtonsPanelButton) => {
    if (!exportLayer) return
    if (button.type !== 'export') return
    if (button.disabled) return

    try {
      const format: VariableItemsExportFormat =
        (button as Extract<ButtonsPanelButton, { type: 'export' }>).format ?? 'xlsx'
      const { blob, filename } = await exportVariableItems(
        {
          variable: exportLayer.variable,
          filter: exportLayer.variable.filter ?? undefined,
          highlight: exportLayer.variable.highlight ?? undefined,
          space: exportLayer.space,
          time: exportLayer.time,
          // select: exportLayer.variable.detailedSelect ?? undefined,
        },
        format,
      )

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toastSuccess('Exportação concluída com sucesso!')
    } catch (error) {
      console.error('[InsightsModal] Export error', error)
      toastError('Não foi possível exportar os itens. Tente novamente.')
    }
  }

  return (
    <OverlayModal
      isOpen={isOpen}
      onRequestClose={handleOnClose}
      className='prix-modal-sentinel-insights'
      overlayClassName='prix-modal-sentinel-insights-overlay'
      style={{
        content: {
          background: 'transparent',
          boxShadow: 'none',
          height: '100%',
        },
      }}
    >
      <InsightListModalWrapper>
        <TitleContainer>
          <h3>Insights</h3>
          <div className='wrapper-buttons'>
            <button className='button-style' onClick={handleOnClose} title='Fechar'>
              <CloseIcon width={32} height={32} />
            </button>
            <RightButtonTopBoxWrapper
              title='Menu de opções'
              className='button-style'
            >
              <RightButtonsTopBox
                onChangeMapType={onChangeMapType}
                mapType={mapType}
                onChangeMap3DOption={onChangeMap3DOption}
                map3DOption={map3DOption}
                layers={layers}
                variables={variables}
                setBaseLayers={setBaseLayers}
                styles={styles}
                keepMenuIcon={true}
              />
            </RightButtonTopBoxWrapper>
          </div>
        </TitleContainer>

        <MobileContainer>
          <Container ref={containerRef} $isMobile={isMobile}>
            {layers.map((layer, layerIndex) =>
              (() => {
                const baseInsights = conclusionsPerLayer[layerIndex]?.insights ?? []
                // Remove insights de exportação anteriores para evitar duplicação
                const filteredBaseInsights = baseInsights.filter(
                  insight => insight.type !== 'exportItems',
                )
                const insightsToRender =
                  layerIndex === exportLayerIndex && exportInsight
                    ? [...filteredBaseInsights, exportInsight]
                    : filteredBaseInsights

                if (!insightsToRender.length) {
                  return (
                    <EmptyState>
                      <p>
                        {isLoading
                          ? 'Carregando...'
                          : 'Nenhum insight disponível no momento para "' +
                            layer.variable.title +
                            '".'}
                      </p>
                    </EmptyState>
                  )
                }

                return (
                  <InsightsList>
                    {insightsToRender.map((insight, insightIndex) => {
                      const isExpanded =
                        selectedInsight?.insightIndex === insightIndex &&
                        selectedInsight?.baseLayerIndex === layerIndex
                      const isDisabled = selectedInsight !== null && !isExpanded
                      const isExportInsight =
                        layerIndex === exportLayerIndex && insight.type === 'exportItems'

                      // Preparar insight para renderização
                      const displayInsight = (() => {
                        if (!isExportInsight) return insight
                        if ((!isExpanded && !isExportInsight) || isLoading)
                          return { ...insight, visualizations: [] }

                        return insight
                      })()

                      return (
                        <InsightCard
                          key={insight.id || insight.key || `${layerIndex}-${insightIndex}`}
                          insight={displayInsight}
                          data-insight-index={insightIndex}
                          isExpanded={isExpanded}
                          isDisabled={isDisabled}
                          isClickable={!isExportInsight}
                          onSelect={() => handleSelectInsight(insight, insightIndex, layerIndex)}
                          variablePreview={selectedInsight?.preview ?? null}
                          onVariablePreviewChange={handleVariablePreviewChange}
                          onActionButtonClick={
                            isExportInsight ? handleInsightButtonClick : undefined
                          }
                        />
                      )
                    })}
                  </InsightsList>
                )
              })(),
            )}

            <FullscreenLoader message='Exportando itens...' isActive={isExporting} />
          </Container>
        </MobileContainer>
      </InsightListModalWrapper>
    </OverlayModal>
  )
}
